import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserStateContext } from "@/contexts/UserStateProvider";
import {
  EXPENSE_ITEM_LIST,
  useExpenseItemList,
  useExpenseItemService,
} from "@/hooks/expenseItem.hooks";
import { useForm } from "@/hooks/form.hook";
import { TextFieldElement } from "@/components/formElements/text-field-element";
import { SelectElement } from "@/components/formElements/select-element";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Menu,
  Search,
  X,
  Plus,
  Trash2,
  XCircleIcon,
  CheckCircleIcon,
  CalendarIcon,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment";
import { useToast } from "@/hooks/toast.hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { TextAreaElement } from "@/components/formElements/text-area-element";
import { LITE_DASHBOARD_STATISTICS } from "@/hooks/tiktokOrder.hooks";
import { LITE_SETTLED_DASHBOARD_STATISTICS } from "@/hooks/tiktokSkuSettledPeriod.hooks";
import { _Button } from "@/components/button";

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const ExpenseItems = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { logOut, isSidebarOpen, setIsSidebarOpen } =
    useContext(UserStateContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pageSize = 10;
  const debouncedSearch = useDebounce(search, 500);

  const { expenseItems, isLoading, error, isFetched } = useExpenseItemList(
    currentPage,
    pageSize,
    debouncedSearch
  );

  const { logItem, deleteItem } = useExpenseItemService();

  const form = useForm({
    fields: {
      label: "",
      description: "",
      category: "ADVERTISING",
      date: moment().format("YYYY-MM-DD"),
    },
  });

  const categoryOptions = [
    { value: "ADVERTISING", label: "Advertising" },
    { value: "SHIPPING", label: "Shipping" },
    { value: "FULFILMENT_FEES", label: "Fulfilment Fees" },
    { value: "OTHER", label: "Other" },
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "ADVERTISING":
        return "bg-emerald-500/10 text-emerald-500";
      case "SHIPPING":
        return "bg-blue-500/10 text-blue-500";
      case "FULFILMENT_FEES":
        return "bg-purple-500/10 text-purple-500";
      default:
        return "bg-gray-500/10 text-gray-500";
    }
  };

  const formatCategoryLabel = (category: string) => {
    switch (category) {
      case "ADVERTISING":
        return "Advertising";
      case "SHIPPING":
        return "Shipping";
      case "FULFILMENT_FEES":
        return "Fulfilment Fees";
      default:
        return "Other";
    }
  };

  const handleSubmit = async () => {
    try {
      const formData = form.state;
      const dateString = moment(formData.date).format("YYYY-MM-DD");

      const response = await logItem(
        formData.label,
        formData.description || undefined,
        formData.category,
        Number(formData.amount),
        "GBP",
        "ONE_TIME",
        dateString
      );

      if (!response.success) {
        toast({
          icon: <XCircleIcon size={12} className={"text-red-500"} />,
          variant: "destructive",
          title: "Uh oh! Something went wrong.",
          description: response.errorMessage || "Failed to log expense item.",
        });
        return;
      }

      setIsModalOpen(false);
      form.setState({});
      toast({
        icon: <CheckCircleIcon size={12} className={"text-green-500"} />,
        variant: "default",
        title: "Success",
        description: "Your expense item has been successfully logged.",
        duration: 2000,
      });
      queryClient.invalidateQueries({
        queryKey: [EXPENSE_ITEM_LIST],
      });
      queryClient.invalidateQueries({
        queryKey: [LITE_DASHBOARD_STATISTICS],
      });
      queryClient.invalidateQueries({
        queryKey: [LITE_SETTLED_DASHBOARD_STATISTICS],
      });
    } catch (error) {
      console.error("Error logging expense item:", error);
    }
  };

  const [isDeletingIds, setIsDeletingIds] = useState<number[]>([]);

  const handleDelete = async (id: number) => {
    try {
      setIsDeletingIds([...isDeletingIds, id]);
      const response = await deleteItem(id);

      if (!response.success) {
        toast({
          icon: <XCircleIcon size={12} className={"text-red-500"} />,
          variant: "destructive",
          title: "Uh oh! Something went wrong.",
          description:
            response.errorMessage || "Failed to delete expense item.",
        });
        return;
      }

      toast({
        icon: <CheckCircleIcon size={12} className={"text-green-500"} />,
        variant: "default",
        title: "Success",
        description: "Expense item has been successfully deleted.",
        duration: 2000,
      });
      queryClient.invalidateQueries({
        queryKey: [EXPENSE_ITEM_LIST],
      });
      queryClient.invalidateQueries({
        queryKey: [LITE_DASHBOARD_STATISTICS],
      });
      queryClient.invalidateQueries({
        queryKey: [LITE_SETTLED_DASHBOARD_STATISTICS],
      });
    } catch (error) {
      console.error("Error deleting expense item:", error);
      toast({
        icon: <XCircleIcon size={12} className={"text-red-500"} />,
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: "An error occurred while deleting the expense item.",
      });
    } finally {
      setIsDeletingIds(isDeletingIds.filter((id) => id !== id));
    }
  };

  const renderPagination = () => {
    return (
      expenseItems && (
        <>
          <Pagination className="flex justify-end">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  className="cursor-pointer rounded-full bg-background border hover:bg-muted/50 transition-colors"
                />
              </PaginationItem>
              {(() => {
                const totalPages = expenseItems.totalPages;
                const currentPageNum = expenseItems.currentPage;
                let startPage = Math.max(currentPageNum - 1, 1);
                let endPage = Math.min(startPage + 2, totalPages);

                if (endPage - startPage < 2) {
                  startPage = Math.max(endPage - 2, 1);
                }

                return Array.from(
                  { length: endPage - startPage + 1 },
                  (_, i) => startPage + i
                ).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => setCurrentPage(page)}
                      isActive={page === currentPageNum}
                      className={`rounded-full cursor-pointer ${
                        page === currentPageNum
                          ? "bg-blue-500/50"
                          : "bg-background hover:bg-muted/50"
                      } border transition-colors`}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ));
              })()}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  className="cursor-pointer rounded-full bg-background border hover:bg-muted/50 transition-colors"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </>
      )
    );
  };

  return (
    <div className="min-h-screen flex-1 flex flex-col overflow-hidden">
      <header className="h-16 bg-background border-b flex items-center justify-between px-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <div className="flex flex-col">
            <h1 className="text-xl font-semibold text-foreground">
              Expense Items
            </h1>
            <div className="flex items-center gap-4">
              <Info className="h-3 w-3 text-blue-500 shrink-0" />
              <div className="text-sm text-muted-foreground">
                Log expenses that Mergoio cannot automatically track.
              </div>
            </div>
          </div>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
              <Plus className="w-4 h-4 mr-2" />
              Log Expense
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader className="space-y-3">
              <DialogTitle className="text-xl font-semibold">
                Log New Expense Item
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Add a new expense item to track your business costs.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-1 gap-6">
                <TextFieldElement
                  form={form}
                  fieldName="label"
                  label="Expense Label"
                  placeholder="e.g., Office supplies, Marketing campaign"
                  required
                  className="w-full"
                />
                <TextAreaElement
                  form={form}
                  rows={3}
                  fieldName="description"
                  label="Description"
                  placeholder="Add any additional details (optional)"
                  className="w-full"
                />
                <SelectElement
                  form={form}
                  fieldName="category"
                  label="Category"
                  options={categoryOptions}
                  placeholder="Select expense category"
                  className="w-full"
                  helper="If selected, the expense will be recorded for the selected category on the dashboard."
                />
                <TextFieldElement
                  form={form}
                  fieldName="amount"
                  label="Amount (£)"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  required
                  className="w-full"
                />
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Record from date
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !form.state.date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.state.date ? (
                          moment(form.state.date).format("DD/MM/YYYY")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          form.state.date
                            ? new Date(form.state.date)
                            : undefined
                        }
                        onSelect={(date) => {
                          if (date) {
                            form.setState({
                              ...form.state,
                              date: moment(date).format("YYYY-MM-DD"),
                            });
                          }
                        }}
                        disabled={(date: Date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
            <DialogFooter className="flex flex-row gap-3 pt-6">
              <Button
                onClick={() => setIsModalOpen(false)}
                variant="outline"
                className="flex-1 h-10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
              >
                Log Expense
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <main className="flex-1 overflow-hidden">
        <div className="mt-6 px-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search expense items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-10 w-[600px]"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {renderPagination()}
          </div>
          <div className="w-full">
            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : error ? (
              <div className="text-red-500 font-semibold">
                Error: {error.message}
              </div>
            ) : (
              <>
                <div className="bg-background rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="font-semibold text-foreground/70">
                          Label
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Category
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Amount
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Date
                        </TableHead>
                        <TableHead className="font-semibold text-foreground/70">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenseItems &&
                      expenseItems.items &&
                      expenseItems.items.length > 0 ? (
                        expenseItems.items.map((expenseRecord: any) => (
                          <TableRow
                            key={expenseRecord.id}
                            className="hover:bg-muted/50 transition-colors"
                          >
                            <TableCell className="py-4">
                              <div>
                                <h3 className="font-medium text-foreground">
                                  {expenseRecord.expenseItem.label}
                                </h3>
                                {expenseRecord.expenseItem.description && (
                                  <p className="text-sm text-foreground/60 mt-1">
                                    {expenseRecord.expenseItem.description}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(expenseRecord.expenseItem.category)}`}>
                                {formatCategoryLabel(expenseRecord.expenseItem.category)}
                              </div>
                            </TableCell>
                            <TableCell className="text-foreground font-semibold">
                              {expenseRecord.currency} {expenseRecord.amount}
                            </TableCell>
                            <TableCell className="text-foreground/80 font-semibold">
                              {moment(expenseRecord.date).format("MMM D, YYYY")}
                            </TableCell>
                            <TableCell>
                              <_Button
                                variant="ghost"
                                size="sm"
                                loading={isDeletingIds.includes(expenseRecord.id)}
                                onClick={() => handleDelete(expenseRecord.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </_Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="text-center py-8 text-foreground/60"
                          >
                            No expense items found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
            <div className="my-8">{renderPagination()}</div>
          </div>
        </div>
      </main>
    </div>
  );
};
